import classNames from "classnames";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import useSlidingIndicator from "../../../shared/hooks/useSlidingIndicator";
import KuikaInput from "../kuika-input/kuika-input";
import KuikaOverflowText from "../kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../kuika-svg/kuika-svg";
import "./kuika-entity-picker.scss";

/**
 * The "which one" panel every switcher in the designer opens: a search (with the create control
 * beside it), optional count-bearing chips, a folder tree with the open record marked CURRENT and
 * a "…" menu on every row, and a footer that counts what is listed and leads to the tool's list.
 *
 * It is the UI Design screen selector's panel, made reusable. That panel is still its own
 * component — it carries drag-and-drop into folders, a RECENT band and a report mode no switcher
 * needs — but it wears this stylesheet, so the two cannot drift.
 *
 * One behaviour differs on purpose: HERE the chips FILTER the list; in the screen selector they
 * JUMP to a section. The screen selector's groups are disjoint sections of one page (plain, web,
 * workflow, master screens); a switcher's groups cut across its folders, so jumping would land in
 * the middle of a folder. Do not "fix" one into the other.
 */

/** Folder rows indent by this per level; a record without a chevron is pushed one slot further. */
const INDENT_PX: number = 16;
/** From the row's edge; the list stands --gap-xs in from the panel's, so content starts 12px in, with the section labels. */
const BASE_PADDING_PX: number = 8;
/** A record has no chevron, so it is padded by the chevron's slot (12px icon + 4px gap). */
const LEAF_SPACER_PX: number = 16;

export interface KuikaPickerItem {
  id: string;
  name: string;
}

/** Membership is a list of ids on the folder, the way the project stores every folder kind. */
export interface KuikaPickerFolder {
  id: string;
  name: string;
  itemIds: string[];
  children?: KuikaPickerFolder[];
}

export interface KuikaPickerChip {
  key: string;
  label: string;
  count: number;
}

export interface KuikaEntityPickerProps<T extends KuikaPickerItem> {
  /** What is listed — already narrowed by the query and the chip. */
  items: T[];
  /** Omit when the kind has no folders: the rows then sit flush, with no chevron slot. */
  folders?: KuikaPickerFolder[];
  currentId?: string;
  /**
   * A query or a chip is narrowing the list: folders with nothing left in them disappear, and
   * every remaining one opens, since the match is what the reader is looking for.
   */
  isFiltering: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  searchPlaceholder: string;
  searchTestId?: string;
  /** The "+" beside the search — a button, or a KuikaDropdownMenu of "New X / New folder". */
  createControl?: React.ReactNode;
  chips?: KuikaPickerChip[];
  activeChip?: string;
  onChipChange?: (key: string) => void;
  chipTestId?: (key: string) => string;
  renderItemIcon: (item: T) => React.ReactNode;
  /** Small marks that belong to the name — a protected branch's lock, a pending switch's spinner. */
  renderItemTrailing?: (item: T) => React.ReactNode;
  onSelect: (item: T, event: React.SyntheticEvent<HTMLElement>) => void;
  /** The row's "…" menu. Report its open state so the row keeps its hover look behind it. */
  renderItemMenu?: (item: T, onOpenChange: (open: boolean) => void) => React.ReactNode;
  renderFolderMenu?: (folder: KuikaPickerFolder, onOpenChange: (open: boolean) => void) => React.ReactNode;
  itemTestId?: (item: T) => string;
  /** A testid for the row's name element, when a test needs to match a row by its text alone. */
  itemNameTestId?: string;
  emptyText: string;
  /** "12 scheduled jobs" or "10 screens · 0 folders". */
  footerCount: string;
  manageLabel?: string;
  onManage?: () => void;
  manageTestId?: string;
  panelTestId?: string;
  className?: string;
}

/** Every folder on the way down to `itemId`, outermost first. */
const findFolderPath = (folders: KuikaPickerFolder[], itemId: string): string[] => {
  for (const folder of folders) {
    if (folder.itemIds.includes(itemId)) return [folder.id];
    const inner: string[] = findFolderPath(folder.children ?? [], itemId);
    if (inner.length > 0) return [folder.id, ...inner];
  }
  return [];
};

const collectFolderItemIds = (folders: KuikaPickerFolder[], into: Set<string>): Set<string> => {
  folders.forEach((folder: KuikaPickerFolder): void => {
    folder.itemIds.forEach((id: string): void => {
      into.add(id);
    });
    collectFolderItemIds(folder.children ?? [], into);
  });
  return into;
};

export const KuikaEntityPicker = <T extends KuikaPickerItem>({
  items,
  folders,
  currentId,
  isFiltering,
  query,
  onQueryChange,
  searchPlaceholder,
  searchTestId,
  createControl,
  chips,
  activeChip,
  onChipChange,
  chipTestId,
  renderItemIcon,
  renderItemTrailing,
  onSelect,
  renderItemMenu,
  renderFolderMenu,
  itemTestId,
  itemNameTestId,
  emptyText,
  footerCount,
  manageLabel,
  onManage,
  manageTestId,
  panelTestId,
  className
}: KuikaEntityPickerProps<T>) => {
  const listRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const hasFolders: boolean = !!folders && folders.length > 0;

  // Folders start closed, except the ones on the way to the open record — the panel opens on
  // where you are, the way the screen selector does. The panel mounts fresh on every open (the
  // popover unmounts its content on close), so the initial state is the only place this is needed.
  const [expanded, setExpanded] = useState<Record<string, boolean>>((): Record<string, boolean> => {
    if (!folders || !currentId) return {};
    return Object.fromEntries(findFolderPath(folders, currentId).map((id: string): [string, boolean] => [id, true]));
  });
  const [openMenuKey, setOpenMenuKey] = useState<string | undefined>(undefined);

  // The open record's fill slides between rows. Re-measured after anything that moves rows without
  // resizing their container (a chip or a query swapping rows of equal count, a folder toggle).
  const rowsRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const remeasureIndicator: () => void = useSlidingIndicator(rowsRef, currentId ?? null, {
    activeSelector: '[aria-current="true"]'
  });
  useLayoutEffect((): void => {
    remeasureIndicator();
  }, [items, expanded, isFiltering, remeasureIndicator]);

  const byId: Map<string, T> = useMemo(
    (): Map<string, T> => new Map(items.map((item: T): [string, T] => [item.id, item])),
    [items]
  );
  const filedIds: Set<string> = useMemo(
    (): Set<string> => collectFolderItemIds(folders ?? [], new Set<string>()),
    [folders]
  );

  // The open record in the middle of the list the moment the panel appears. Layout effect, so the
  // first frame the reader sees is already scrolled; the path to it was opened in the initial state.
  useLayoutEffect((): void => {
    const list: HTMLDivElement | null = listRef.current;
    const row: HTMLElement | null = list?.querySelector<HTMLElement>('[data-picker-current="true"]') ?? null;
    if (!list || !row) return;
    // From the rects, not `offsetTop`: a row is `position: relative`, so its offsetParent is the
    // popover and the search above the list would land every target a search-height too low.
    const rowTop: number = row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    list.scrollTop = rowTop - (list.clientHeight - row.offsetHeight) / 2;
  }, []);

  const menuHandler =
    (key: string) =>
    (open: boolean): void =>
      setOpenMenuKey((prev: string | undefined): string | undefined => {
        if (open) return key;
        return prev === key ? undefined : prev;
      });

  /**
   * Whether a click on a row is the row's own. A click in its "…" is the menu's — and so is one in the
   * menu's popup, which is portaled out of the row's DOM but still bubbles up the React tree to it: a pick
   * there (the table switcher's Browse Data) used to arrive here as a pick of the row too. A click whose
   * target is outside the row's DOM came through such a portal.
   */
  const isRowOwnClick = (event: React.MouseEvent<HTMLElement>): boolean => {
    const target: EventTarget = event.target;
    if (!(target instanceof Element) || !event.currentTarget.contains(target)) return false;
    return !target.closest(".kuika-picker__row-actions");
  };

  const activateOnKey = (event: React.KeyboardEvent<HTMLElement>, run: () => void): void => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    run();
  };

  const countVisible = (folder: KuikaPickerFolder): number =>
    folder.itemIds.filter((id: string): boolean => byId.has(id)).length +
    (folder.children ?? []).reduce((sum: number, child: KuikaPickerFolder): number => sum + countVisible(child), 0);

  const renderItem = (item: T, depth: number): React.ReactNode => {
    const isCurrent: boolean = item.id === currentId;
    const trailing: React.ReactNode = renderItemTrailing?.(item);
    const menuKey: string = `item:${item.id}`;
    const padding: number = BASE_PADDING_PX + depth * INDENT_PX + (hasFolders ? LEAF_SPACER_PX : 0);
    return (
      <div
        key={item.id}
        role="button"
        tabIndex={0}
        className={classNames("kuika-picker__row", "kuika-picker__row--item", OVERFLOW_REVEAL_HOST_CLASS, {
          "kuika-picker__row--current": isCurrent,
          "kuika-picker__row--menu-open": openMenuKey === menuKey
        })}
        style={{ paddingLeft: `${padding}px` }}
        aria-current={isCurrent ? "true" : undefined}
        data-picker-current={isCurrent ? "true" : undefined}
        data-testid={itemTestId?.(item)}
        onClick={(event: React.MouseEvent<HTMLDivElement>): void => {
          // A click inside the row's own menu is the menu's, not a pick.
          if (!isRowOwnClick(event)) return;
          onSelect(item, event);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): void =>
          activateOnKey(event, (): void => onSelect(item, event))
        }
      >
        {renderItemIcon(item)}
        <span className="kuika-picker__name" data-testid={itemNameTestId}>
          <KuikaOverflowText>{item.name}</KuikaOverflowText>
          {/* Inside the name, so the marks follow its last letter rather than the row's end. */}
          {trailing && <span className="kuika-picker__name-trailing">{trailing}</span>}
        </span>
        {/* CURRENT sits at the right end and is pushed inwards by the "…" on hover. */}
        {isCurrent && <span className="kuika-picker__current-badge">Current</span>}
        {renderItemMenu && (
          <span className="kuika-picker__row-actions">{renderItemMenu(item, menuHandler(menuKey))}</span>
        )}
      </div>
    );
  };

  const renderFolder = (folder: KuikaPickerFolder, depth: number): React.ReactNode => {
    const visibleCount: number = countVisible(folder);
    // A narrowed list only shows the folders that still hold something.
    if (isFiltering && visibleCount === 0) return null;
    const isOpen: boolean = isFiltering || !!expanded[folder.id];
    const menuKey: string = `folder:${folder.id}`;
    const toggle = (): void =>
      setExpanded((prev: Record<string, boolean>) => ({ ...prev, [folder.id]: !prev[folder.id] }));
    const folderItems: T[] = folder.itemIds
      .map((id: string): T | undefined => byId.get(id))
      .filter((item: T | undefined): item is T => !!item);

    return (
      <div key={folder.id} className="kuika-picker__node">
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          className={classNames("kuika-picker__row", "kuika-picker__row--folder", OVERFLOW_REVEAL_HOST_CLASS, {
            "kuika-picker__row--menu-open": openMenuKey === menuKey
          })}
          style={{ paddingLeft: `${BASE_PADDING_PX + depth * INDENT_PX}px` }}
          onClick={(event: React.MouseEvent<HTMLDivElement>): void => {
            if (!isRowOwnClick(event)) return;
            if (!isFiltering) toggle();
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): void =>
            activateOnKey(event, (): void => {
              if (!isFiltering) toggle();
            })
          }
        >
          <KuikaSvg name={isOpen ? "chevron-down" : "chevron-right"} type="arrows" width={12} height={12} />
          <KuikaSvg name={isOpen ? "folder-open" : "folder"} type="sidebar-icons" width={16} height={16} />
          <span className="kuika-picker__name">
            <KuikaOverflowText>{folder.name}</KuikaOverflowText>
          </span>
          {/* The count owns the right end at rest; on hover the "…" takes its place. */}
          <span className="kuika-picker__count">{visibleCount}</span>
          {renderFolderMenu && (
            <span className="kuika-picker__row-actions">{renderFolderMenu(folder, menuHandler(menuKey))}</span>
          )}
        </div>
        <div className={classNames("kuika-picker__children", { "kuika-picker__children--collapsed": !isOpen })}>
          {isOpen && (
            <>
              {(folder.children ?? []).map((child: KuikaPickerFolder) => renderFolder(child, depth + 1))}
              {folderItems.map((item: T) => renderItem(item, depth + 1))}
            </>
          )}
        </div>
      </div>
    );
  };

  const unfiled: T[] = items.filter((item: T): boolean => !filedIds.has(item.id));
  const hasRows: boolean =
    unfiled.length > 0 ||
    (folders ?? []).some((folder: KuikaPickerFolder): boolean => !isFiltering || countVisible(folder) > 0);

  return (
    <div className={classNames("kuika-picker", className)} data-testid={panelTestId}>
      {/* Keys stay here: typing in the search must not reach a host's keyboard handling. */}
      <div
        className={classNames("kuika-picker__search", { "kuika-picker__search--divided": !chips?.length })}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): void => {
          if (event.key !== "Escape") event.stopPropagation();
        }}
      >
        <KuikaInput
          size="md"
          data-testid={searchTestId}
          leftIcon={<KuikaSvg name="search" width={16} height={16} />}
          placeholder={searchPlaceholder}
          value={query}
          onChange={(_event: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
            onQueryChange(String(value ?? ""))
          }
        />
        {createControl}
      </div>

      {!!chips?.length && (
        <div className="kuika-picker__chips" role="group">
          {chips.map((chip: KuikaPickerChip) => {
            const isActive: boolean = chip.key === activeChip;
            return (
              <button
                key={chip.key}
                type="button"
                aria-pressed={isActive}
                data-testid={chipTestId?.(chip.key)}
                className={classNames("kuika-picker__chip", { "kuika-picker__chip--active": isActive })}
                onClick={(): void => onChipChange?.(chip.key)}
              >
                <span>{chip.label}</span>
                {/* An empty chip stays a plain label — a framed "0" is noise. */}
                {chip.count > 0 && <span className="kuika-picker__chip-count">{chip.count}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className="kuika-picker__list" ref={listRef}>
        <div className="kuika-picker__rows" ref={rowsRef}>
          <span className="kuika-sliding-indicator kuika-picker__indicator" aria-hidden="true" />
          {hasRows ? (
            <>
              {(folders ?? []).map((folder: KuikaPickerFolder) => renderFolder(folder, 0))}
              {unfiled.map((item: T) => renderItem(item, 0))}
            </>
          ) : (
            <div className="kuika-picker__empty">{emptyText}</div>
          )}
        </div>
      </div>

      <div className="kuika-picker__footer">
        <span className="kuika-picker__footer-count" title={footerCount}>
          {footerCount}
        </span>
        {onManage && manageLabel && (
          <button type="button" className="kuika-picker__manage" data-testid={manageTestId} onClick={onManage}>
            <KuikaSvg name="settings" type="sidebar-icons" width={16} height={16} />
            {manageLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default KuikaEntityPicker;
